"use client";

import type { ReactNode } from "react";
import type { DraggableProvidedDragHandleProps, DropResult } from "react-beautiful-dnd";
import { Draggable } from "react-beautiful-dnd";
import { StrictModeDroppable } from "@/@libs/strict-mode-droppable";
import {
  iHolidayQuoteItineraryBlock,
  type QuoteNarrativeLayoutVariant,
  type TripItineraryOrder,
} from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { Plane } from "lucide-react";
import { getItineraryBlockDate } from "../../helpers/prefill-day-by-day-itinerary";
import { scrollToItineraryBlock } from "../helpers/scroll-to-itinerary-block";
import { ItineraryBlock } from "../itinerary-section";
import { quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { useQuoteThemeStyles } from "../quote-theme/use-quote-theme-styles";
import { partitionItineraryForTripMode } from "./partition-itinerary-for-trip-mode";
import { TripModeAccommodationsSection } from "./trip-mode-accommodations-section";
import { TripModeFlightsSection } from "./trip-mode-flights-section";
import { TripModeStaysSummary } from "./trip-mode-stays-summary";

export const TRIP_ITINERARY_DROPPABLE_IDS = {
  hotels: "trip-hotels",
  flights: "trip-flights",
  other: "trip-other",
  chronological: "trip-chronological",
} as const;

interface TripModeItinerarySectionProps {
  itinerary: iHolidayQuoteItineraryBlock[];
  renderHotel?: (
    hotel: iHolidayQuoteItineraryBlock,
    index: number,
    card: ReactNode,
    dragHandleProps?: DraggableProvidedDragHandleProps | null
  ) => ReactNode;
  renderFlight?: (
    flight: iHolidayQuoteItineraryBlock,
    index: number,
    card: ReactNode,
    dragHandleProps?: DraggableProvidedDragHandleProps | null
  ) => ReactNode;
  renderOther?: (
    item: iHolidayQuoteItineraryBlock,
    index: number,
    card: ReactNode,
    dragHandleProps?: DraggableProvidedDragHandleProps | null
  ) => ReactNode;
  footer?: ReactNode;
  onAccommodationsLayoutChange?: (
    layout: QuoteNarrativeLayoutVariant
  ) => Promise<void> | void;
  onFlightsLayoutChange?: (layout: QuoteNarrativeLayoutVariant) => Promise<void> | void;
  tripItineraryOrder?: TripItineraryOrder | null;
  onTripItineraryOrderChange?: (
    order: TripItineraryOrder
  ) => Promise<void> | void;
  onEditItinerary?: (blocks: iHolidayQuoteItineraryBlock[]) => Promise<void> | void;
}

export function reorderItineraryWithinType(
  itinerary: iHolidayQuoteItineraryBlock[],
  typePredicate: (item: iHolidayQuoteItineraryBlock) => boolean,
  reorderedIds: string[]
) {
  const reorderedById = new Map(
    reorderedIds
      .map((id) => itinerary.find((item) => item.id === id))
      .filter((item): item is iHolidayQuoteItineraryBlock => Boolean(item))
      .map((item) => [item.id, item])
  );
  const nextTypeItems = reorderedIds
    .map((id) => reorderedById.get(id))
    .filter((item): item is iHolidayQuoteItineraryBlock => Boolean(item));
  let typeIndex = 0;

  return itinerary.map((item) => {
    if (!typePredicate(item)) return item;
    return nextTypeItems[typeIndex++] ?? item;
  });
}

export function getTripItineraryReorderUpdate(
  itinerary: iHolidayQuoteItineraryBlock[],
  result: DropResult
) {
  const { destination, source } = result;
  if (!destination || destination.droppableId !== source.droppableId) return null;
  if (destination.index === source.index) return null;

  if (source.droppableId === TRIP_ITINERARY_DROPPABLE_IDS.chronological) {
    const sorted = getChronologicalItinerary(itinerary);
    const items = Array.from(sorted);
    const [moved] = items.splice(source.index, 1);
    items.splice(destination.index, 0, moved);
    return items;
  }

  const { hotels, flights, other } = partitionItineraryForTripMode(itinerary);
  const bucket =
    source.droppableId === TRIP_ITINERARY_DROPPABLE_IDS.hotels
      ? hotels
      : source.droppableId === TRIP_ITINERARY_DROPPABLE_IDS.flights
        ? flights
        : source.droppableId === TRIP_ITINERARY_DROPPABLE_IDS.other
          ? other
          : null;
  if (!bucket) return null;

  const items = Array.from(bucket);
  const [moved] = items.splice(source.index, 1);
  items.splice(destination.index, 0, moved);
  const reorderedIds = items.map((item) => item.id);

  return reorderItineraryWithinType(
    itinerary,
    (item) =>
      source.droppableId === TRIP_ITINERARY_DROPPABLE_IDS.hotels
        ? item.type === "hotel"
        : source.droppableId === TRIP_ITINERARY_DROPPABLE_IDS.flights
          ? item.type === "flight"
          : item.type !== "hotel" && item.type !== "flight",
    reorderedIds
  );
}

function getChronologicalItinerary(itinerary: iHolidayQuoteItineraryBlock[]) {
  return [...itinerary].sort((a, b) => {
    const dateA = getItineraryBlockDate(a) || "9999-12-31";
    const dateB = getItineraryBlockDate(b) || "9999-12-31";
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return itinerary.findIndex((item) => item.id === a.id) -
      itinerary.findIndex((item) => item.id === b.id);
  });
}

function flightSummaryLine(flights: iHolidayQuoteItineraryBlock[]) {
  if (flights.length === 0) return null;
  const routes = flights
    .slice(0, 3)
    .map((flight) => {
      const data = flight.flightData;
      return `${data?.departureAirport || "?"} → ${data?.arrivalAirport || "?"}`;
    });
  const extra = flights.length > routes.length ? ` +${flights.length - routes.length} more` : "";
  return `${flights.length} flight${flights.length === 1 ? "" : "s"} · ${routes.join(", ")}${extra}`;
}

function TripOverviewStrip({
  hotels,
  flights,
}: {
  hotels: iHolidayQuoteItineraryBlock[];
  flights: iHolidayQuoteItineraryBlock[];
}) {
  const flightsLine = flightSummaryLine(flights);
  if (hotels.length === 0 && !flightsLine) return null;

  return (
    <section
      className="scroll-mt-28 bg-white p-4 sm:p-6"
      style={{
        borderRadius: quoteThemeCssVars.radiusCard,
        boxShadow: quoteThemeCssVars.shadowCard,
      }}
    >
      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(240px,0.8fr)]">
        <TripModeStaysSummary
          hotels={hotels}
          onSelectStay={(blockId) => scrollToItineraryBlock(blockId)}
        />
        {flightsLine ? (
          <div className="space-y-3">
            <h3
              className="text-sm font-semibold uppercase tracking-wide"
              style={{ color: quoteThemeCssVars.primary }}
            >
              Your flights
            </h3>
            <div className="flex items-start gap-3 rounded-xl bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
              <Plane
                className="mt-0.5 h-4 w-4 shrink-0"
                style={{ color: quoteThemeCssVars.primary }}
              />
              <span>{flightsLine}</span>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function TripModeItinerarySection({
  itinerary,
  renderHotel,
  renderFlight,
  renderOther,
  footer,
  onAccommodationsLayoutChange,
  onFlightsLayoutChange,
  tripItineraryOrder,
  onTripItineraryOrderChange,
  onEditItinerary,
}: TripModeItinerarySectionProps) {
  const { sectionHeadingClassName, classNames } = useQuoteThemeStyles();
  const { hotels, flights, other } = partitionItineraryForTripMode(itinerary);
  const isEditable = Boolean(footer || onEditItinerary);
  const order: TripItineraryOrder =
    tripItineraryOrder === "chronological" ? "chronological" : "grouped";

  if (hotels.length === 0 && flights.length === 0 && other.length === 0) {
    return (
      <section className={`${classNames.pageBackground} p-4 md:p-6 rounded-3xl`}>
        <h2 className={`${sectionHeadingClassName} mb-2`}>Your Trip</h2>
        <p className="text-sm text-gray-500">
          {isEditable
            ? "Add a hotel or flight to build your brochure."
            : "No itinerary items added yet."}
        </p>
        {footer}
      </section>
    );
  }

  const orderControls = onTripItineraryOrderChange ? (
    <section
      className={`${classNames.pageBackground} flex flex-wrap items-center justify-between gap-3 p-4 md:p-5 rounded-3xl`}
    >
      <div>
        <h2 className={`${sectionHeadingClassName} mb-1`}>Your Trip</h2>
        <p className="text-sm text-gray-500">
          Choose how itinerary items are arranged in Brochure Mode.
        </p>
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-600">
        <span className="hidden sm:inline">Order</span>
        <select
          className="rounded-lg border bg-white px-2 py-1.5 text-sm"
          value={order}
          onChange={(event) =>
            onTripItineraryOrderChange(event.target.value as TripItineraryOrder)
          }
        >
          <option value="grouped">Grouped</option>
          <option value="chronological">Chronological</option>
        </select>
      </label>
    </section>
  ) : null;

  const overview = <TripOverviewStrip hotels={hotels} flights={flights} />;

  if (order === "chronological") {
    const chronological = getChronologicalItinerary(itinerary);
    return (
      <div className="space-y-8">
        {orderControls}
        {overview}
        <section
          className={`scroll-mt-28 ${classNames.pageBackground} p-4 md:p-6 rounded-3xl`}
        >
          <h2 className={`${sectionHeadingClassName} mb-6`}>
            Chronological itinerary
          </h2>
          {onEditItinerary ? (
            <StrictModeDroppable
              droppableId={TRIP_ITINERARY_DROPPABLE_IDS.chronological}
            >
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-6"
                >
                  {chronological.map((item, index) => {
                    const card = (
                      <div
                        key={item.id}
                        id={`itinerary-${item.id}`}
                        className="scroll-mt-28"
                      >
                        <ItineraryBlock item={item} index={index} />
                      </div>
                    );
                    const render =
                      item.type === "hotel"
                        ? renderHotel
                        : item.type === "flight"
                          ? renderFlight
                          : renderOther;
                    return (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                      >
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                          >
                            {render
                              ? render(
                                  item,
                                  index,
                                  card,
                                  dragProvided.dragHandleProps
                                )
                              : card}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          ) : (
            <div className="space-y-6">
              {chronological.map((item, index) => (
                <div
                  key={item.id}
                  id={`itinerary-${item.id}`}
                  className="scroll-mt-28"
                >
                  <ItineraryBlock item={item} index={index} />
                </div>
              ))}
            </div>
          )}
        </section>
        {footer}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {orderControls}
      {overview}
      <TripModeAccommodationsSection
        hotels={hotels}
        renderHotel={renderHotel}
        onLayoutChange={onAccommodationsLayoutChange}
        droppableId={
          onEditItinerary ? TRIP_ITINERARY_DROPPABLE_IDS.hotels : undefined
        }
        showEmpty={isEditable && (flights.length > 0 || other.length > 0)}
      />

      <TripModeFlightsSection
        flights={flights}
        renderFlight={renderFlight}
        onLayoutChange={onFlightsLayoutChange}
        droppableId={
          onEditItinerary ? TRIP_ITINERARY_DROPPABLE_IDS.flights : undefined
        }
        showEmpty={isEditable && (hotels.length > 0 || other.length > 0)}
      />

      {other.length > 0 ? (
        <section
          id="trip-section-experiences"
          className={`scroll-mt-28 ${classNames.pageBackground} p-4 md:p-6 rounded-3xl`}
        >
          <h2 className={`${sectionHeadingClassName} mb-6`}>
            Transfers & Experiences
          </h2>
          {onEditItinerary ? (
            <StrictModeDroppable droppableId={TRIP_ITINERARY_DROPPABLE_IDS.other}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-6"
                >
                  {other.map((item, index) => {
                    const card = (
                      <div
                        key={item.id}
                        id={`itinerary-${item.id}`}
                        className="scroll-mt-28"
                      >
                        <ItineraryBlock item={item} index={index} />
                      </div>
                    );
                    return (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                      >
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                          >
                            {renderOther
                              ? renderOther(
                                  item,
                                  index,
                                  card,
                                  dragProvided.dragHandleProps
                                )
                              : card}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          ) : (
            <div className="space-y-6">
              {other.map((item, index) => {
                const card = (
                  <div
                    key={item.id}
                    id={`itinerary-${item.id}`}
                    className="scroll-mt-28"
                  >
                    <ItineraryBlock item={item} index={index} />
                  </div>
                );
                return renderOther ? renderOther(item, index, card) : card;
              })}
            </div>
          )}
        </section>
      ) : null}

      {footer}
    </div>
  );
}
